<template>
  <div class="group flex flex-col p-3 sm:p-5 bg-white border border-gray-100 rounded-2xl sm:rounded-3xl shadow-sm hover:shadow-xl hover:border-orange-200 transition-all duration-300 overflow-hidden relative">
    <!-- Badge: Cantidad ya agregada al Carrito -->
    <div v-if="cartQuantity > 0" class="absolute top-2.5 left-2.5 sm:top-3.5 sm:left-3.5 z-10">
      <span class="px-2 py-0.5 rounded-lg bg-orange-500 text-white text-[9px] sm:text-[10px] font-black uppercase tracking-tight shadow-md flex items-center gap-1">
        <i class="fas fa-shopping-basket text-[8px]" />
        <span>{{ cartQuantity }}</span>
      </span>
    </div>

    <!-- Badges / Indicador de Stock sutil -->
    <div class="absolute top-2.5 right-2.5 sm:top-3.5 sm:right-3.5 z-10">
      <span v-if="product.type_product === 'service'" class="px-1.5 py-0.5 sm:px-2 sm:py-1 rounded-lg bg-emerald-50 text-emerald-700 text-[8px] sm:text-[10px] font-bold uppercase tracking-tight border border-emerald-200 shadow-sm">
        Servicio
      </span>
      <span v-else-if="product.type_product === 'recipe'" :class="product.quantity > 0 ? 'bg-purple-50 text-purple-700 border-purple-200' : 'bg-rose-50 text-rose-600 border-rose-100'" class="px-1.5 py-0.5 sm:px-2 sm:py-1 rounded-lg text-[8px] sm:text-[10px] font-bold uppercase tracking-tight border shadow-sm flex items-center gap-1">
        <i v-if="product.quantity > 0" class="fas fa-utensils text-[8px]" />
        <span v-if="product.quantity > 0">{{ product.quantity }} disp.</span>
        <span v-else class="font-black tracking-tighter">Agotado</span>
      </span>
      <span v-else-if="product.quantity > 0" class="px-1.5 py-0.5 sm:px-2 sm:py-1 rounded-lg bg-white text-gray-600 text-[8px] sm:text-[10px] font-bold uppercase tracking-tight border border-gray-200 shadow-sm">
        {{ product.quantity }} disp.
      </span>
      <span v-else class="px-1.5 py-0.5 sm:px-2 sm:py-1 rounded-lg bg-rose-50 text-rose-600 text-[8px] sm:text-[10px] font-black uppercase tracking-tighter border border-rose-100 shadow-sm">
        Agotado
      </span>
    </div>

    <!-- Imagen del Producto -->
    <div class="h-28 sm:h-44 w-full rounded-xl sm:rounded-2xl bg-gray-50 flex items-center justify-center mb-2.5 sm:mb-4 overflow-hidden group-hover:scale-105 transition-transform duration-500 p-2 sm:p-4">
      <img 
        v-if="product.photo && !imageError" 
        :src="product.photo" 
        class="max-w-full max-h-full object-contain" 
        alt="" 
        loading="lazy"
        @error="imageError = true" 
      />
      <div v-else class="flex flex-col items-center justify-center text-gray-300">
        <i class="fas fa-utensils text-2xl sm:text-4xl text-orange-200 mb-1" />
        <span class="text-[8px] sm:text-[9px] font-bold text-gray-300 uppercase tracking-wider">Sin foto</span>
      </div>
    </div>

    <!-- Info del Producto -->
    <div class="flex-1 mb-2.5 sm:mb-3">
      <div class="flex mb-1 sm:mb-1.5">
        <span class="px-1.5 py-0.5 rounded-full bg-orange-50 text-orange-600 text-[7px] sm:text-[8px] font-bold uppercase tracking-wider truncate max-w-full">
          {{ product.product_category?.name || product.category_name || 'General' }}
        </span>
      </div>
      <h3 class="text-xs sm:text-[13px] font-bold text-gray-800 leading-tight mb-0.5 group-hover:text-orange-600 transition-colors uppercase tracking-tight line-clamp-2 min-h-[1.75rem] sm:min-h-[2.2rem]">
        {{ product.name }}
      </h3>
      <p class="text-[8px] sm:text-[9px] font-medium text-gray-400 uppercase tracking-widest truncate">{{ product.code || 'S/N' }}</p>
    </div>

    <!-- Sección de Precios -->
    <div class="bg-gray-50 rounded-xl sm:rounded-2xl p-2 sm:p-3.5 mb-2.5 sm:mb-4 border border-gray-100 group-hover:bg-orange-50 transition-colors">
      <div class="flex items-baseline justify-between mb-1">
        <span class="text-[9px] sm:text-xs font-semibold text-gray-400 uppercase tracking-tighter">USD</span>
        <span class="text-sm sm:text-lg font-black text-gray-900">${{ Number(product?.prices?.[0]?.price || 0).toFixed(2) }}</span>
      </div>
      
      <div class="flex justify-between items-baseline pt-1 sm:pt-2 border-t border-gray-200">
        <span class="text-[8px] sm:text-[10px] font-semibold text-gray-400 uppercase tracking-tighter">Bs.</span>
        <span class="text-xs sm:text-sm font-black text-orange-600">Bs. {{ (Number(product?.prices?.[0]?.price || 0) * (change || 1)).toFixed(2) }}</span>
      </div>
    </div>

    <!-- Acción -->
    <button 
      :disabled="product.type_product !== 'service' && product.quantity <= 0" 
      class="w-full py-2.5 sm:py-3.5 rounded-xl sm:rounded-2xl bg-gray-900 text-white font-bold text-[11px] sm:text-xs flex items-center justify-center gap-1.5 sm:gap-2 transition-all hover:bg-orange-500 hover:shadow-md hover:shadow-orange-200 active:scale-95 disabled:opacity-30 disabled:cursor-not-allowed group/btn touch-manipulation"
      @click="addProduct(product)"
    >
      <i class="fas fa-plus-circle text-xs group-hover/btn:rotate-90 transition-transform" />
      <span>{{ cartQuantity > 0 ? '+ Agregar' : 'Agregar' }}</span>
    </button>
  </div>
</template>

<script>
export default {
  props: {
    product: Object,
    change: Number,
    cop_change: Number,
    cartQuantity: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      imageError: false,
    }
  },
  watch: {
    'product.id'() {
      this.imageError = false
    },
  },
  methods: {
    addProduct(product) {
      this.$emit('addCart', product)
    },
  },
}
</script>
